import { sql } from 'drizzle-orm'
import {
  bigint,
  boolean,
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
} from 'drizzle-orm/pg-core'

import { createdAt, primaryId, updatedAt } from './columns.ts'
import { trainingSession } from './training.ts'

/**
 * better-auth-managed tables: user, session, account, verification.
 *
 * Shapes mirror what better-auth's Drizzle adapter expects — P3 only wires the
 * library to them, it does not create them (P2 owns every table in this repo).
 * Re-run `npx @better-auth/cli generate` and reconcile after a version bump.
 *
 * `role` / `banned` / `banReason` / `banExpires` on `user` and `impersonatedBy`
 * on `session` come from the **admin plugin**. FLOU is single-tenant, so the
 * organization plugin's `activeOrganizationId` is deliberately absent.
 *
 * Roles are a comma-separated list (admin-plugin convention), not an enum: the
 * plugin writes the column itself and would not respect a pgEnum.
 */
export const user = pgTable(
  'user',
  {
    id: text('id').primaryKey(),
    name: text('name'),
    email: text('email').notNull().unique(),
    emailVerified: boolean('email_verified').notNull().default(false),
    image: text('image'),

    // ── admin plugin ──────────────────────────────────────────────────────
    /** Comma-separated: `admin`, `staff`, `customer`. Null until P3 sets it. */
    role: text('role'),
    banned: boolean('banned').notNull().default(false),
    banReason: text('ban_reason'),
    banExpires: timestamp('ban_expires', { withTimezone: true }),

    // ── FLOU profile (better-auth `additionalFields` in P3) ────────────────
    // Declared here because P2 owns the table: P3 only describes these columns
    // to better-auth, and P7's dashboard reads them. Every one is nullable —
    // a sign-up collects an email and nothing else.
    /** E.164 or local PL format; validated in the app, not by the column. */
    phone: text('phone'),
    companyName: text('company_name'),
    /** Polish NIP, digits only. */
    taxId: text('tax_id'),
    billingAddressLine1: text('billing_address_line1'),
    billingAddressLine2: text('billing_address_line2'),
    billingPostalCode: text('billing_postal_code'),
    billingCity: text('billing_city'),
    billingCountry: text('billing_country'),
    /** UI locale preference. `pl` is canonical; null means "follow the route". */
    locale: text('locale'),
    /** Set when the user opted into marketing mail. Null = never opted in. */
    marketingConsentAt: timestamp('marketing_consent_at', { withTimezone: true }),

    /**
     * When this person accepted the terms and the privacy policy (spec §3).
     *
     * Two columns rather than one, mirroring `order` and `lead`, because they
     * are two documents with two versions and two legal bases: consent to a
     * contract and acknowledgement of a processing notice. Answering "did they
     * agree to the terms?" must not depend on remembering that one flag covered
     * both.
     *
     * Written by the server's own clock inside the sign-up hook, never from a
     * value the browser sends — a timestamp somebody else chose is not evidence
     * of anything. NULL for every account created before this existed, which is
     * the honest reading: nobody recorded it, so we do not know.
     */
    termsAcceptedAt: timestamp('terms_accepted_at', { withTimezone: true }),
    privacyAcceptedAt: timestamp('privacy_accepted_at', { withTimezone: true }),

    /**
     * When this account last signed in, and when it was last seen using FLOU.
     *
     * ON `user` AND NOT DERIVED FROM `session`, deliberately. The session table
     * looks like it already answers both — `max(createdAt)` is a sign-in,
     * `max(updatedAt)` is a refresh — but it is a table of LIVE cookies, not a
     * history: signing out deletes the row and an expired one is pruned on the
     * next read. Asking it when somebody last logged in answers "never" for
     * anyone who signed in yesterday and then signed out, which is precisely
     * the person the roster is being consulted about.
     *
     * Two columns and not one, because they answer two different questions and
     * the gap between them is the interesting part: a freelancer who logged in
     * in March and was last seen in March has abandoned the panel; one who
     * logged in in March and was seen an hour ago is living in it.
     *
     * NULL means "not recorded", NOT "never": both columns start empty for
     * every account that existed before they did, and the migration backfills
     * only what the surviving `session` rows still remember. The panel says
     * „Nie odnotowano" rather than „Nigdy" for exactly that reason.
     *
     * `lastSeenAt` is written at most once every ten minutes per account
     * (`@repo/auth`'s `getSession`), so this is not a per-request write. The
     * UPDATE additionally refuses to move the mark backwards, so two tabs
     * racing cannot make somebody's activity older than it is.
     */
    lastLoginAt: timestamp('last_login_at', { withTimezone: true }),
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true }),

    /**
     * Set only on an account INVENTED for a training rehearsal — the made-up
     * performer an admin assigns a made-up order to (`training_session`).
     *
     * NULL on every real account, and `CASCADE`, so „Zakończ test" removes the
     * invented people along with the invented work and nobody has to remember
     * a second list.
     *
     * Such an account cannot be signed into: the rehearsal writes no `account`
     * row, so there is no credential to present and no provider to come back
     * from. The admin looks through its eyes with the panel's own role-preview
     * instead. Every screen that lists PEOPLE — the roster, the assignment
     * pickers, the capacity board, the pay run — filters this out, because an
     * invented editor in a real staff list is a mistake waiting for somebody to
     * act on it.
     */
    trainingSessionId: text('training_session_id').references(() => trainingSession.id, {
      onDelete: 'cascade',
    }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('user_role_idx').on(t.role),
    // Asked from two directions: „who did this rehearsal invent?" (teardown)
    // and „is this account real?" (every roster read).
    index('user_training_idx').on(t.trainingSessionId),
  ],
)

export const session = pgTable(
  'session',
  {
    id: text('id').primaryKey(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    token: text('token').notNull().unique(),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
    ipAddress: text('ip_address'),
    userAgent: text('user_agent'),
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    /** admin plugin — set while a staff member is impersonating this user. */
    impersonatedBy: text('impersonated_by'),
  },
  (t) => [index('session_user_idx').on(t.userId)],
)

export const account = pgTable(
  'account',
  {
    id: text('id').primaryKey(),
    accountId: text('account_id').notNull(),
    providerId: text('provider_id').notNull(),
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    idToken: text('id_token'),
    accessTokenExpiresAt: timestamp('access_token_expires_at', { withTimezone: true }),
    refreshTokenExpiresAt: timestamp('refresh_token_expires_at', { withTimezone: true }),
    scope: text('scope'),
    /** Hashed by better-auth; only set for the credentials provider. */
    password: text('password'),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [index('account_user_idx').on(t.userId)],
)

export const verification = pgTable(
  'verification',
  {
    id: text('id').primaryKey(),
    identifier: text('identifier').notNull(),
    value: text('value').notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [index('verification_identifier_idx').on(t.identifier)],
)

/**
 * Request-counting substrate. **P2 creates it because P2 owns every table** —
 * P3 is not allowed to add one, and by the time it needs one it is too late to
 * negotiate: better-auth defaults to `rateLimit.enabled = isProduction` with
 * `storage: 'memory'`, and on Vercel serverless "memory" is per-instance, i.e.
 * effectively no rate limiting at all on the login and password-reset routes.
 * Switching it to `storage: 'database'` requires exactly this model, and Redis
 * is out by decision, so there is no other substrate in this repo.
 *
 * The column set is dictated by better-auth's own database storage
 * (`better-auth/dist/api/rate-limiter/index.mjs`, model `"rateLimit"`): it
 * reads and writes `key`, `count` and `lastRequest`, and prunes with
 * `deleteMany(lastRequest < cutoff)`. P3 points better-auth at it with
 * `rateLimit: { storage: 'database', modelName: 'rate_limit' }`.
 *
 * `key` is UNIQUE and that is load-bearing, not tidiness: the consume path
 * inserts optimistically and, on failure, re-reads and retries
 * (`catch { if (!await readRow(key)) throw error; return consume(...) }`). That
 * recovery only fires if the database rejects the duplicate. Without the unique
 * index two concurrent requests each insert their own row with `count: 1` and
 * the limit never trips — the failure mode is silent and looks like success.
 *
 * `lastRequest` is epoch MILLISECONDS (`Date.now()`), not a timestamp: it is
 * compared arithmetically against a window in ms by the library. `bigint` with
 * `mode: 'number'` because better-auth normalises a bigint back to a number
 * itself, and ms epochs stay well inside `Number.MAX_SAFE_INTEGER`.
 *
 * Deliberately generic — a `key` is any bucket string. P6 (checkout), P9
 * (forms) and P13 (AI chat) share this table rather than inventing their own,
 * which is the whole reason it lives here and not inside the auth phase.
 */
export const rateLimit = pgTable(
  'rate_limit',
  {
    id: primaryId('rl'),
    /** Bucket identifier, e.g. `ip:1.2.3.4:/sign-in/email`. Opaque to us. */
    key: text('key').notNull().unique(),
    /** Requests seen in the current window. */
    count: integer('count').notNull().default(0),
    /** `Date.now()` of the most recent request in the window, in ms. */
    lastRequest: bigint('last_request', { mode: 'number' }).notNull(),
  },
  (t) => [
    // The prune path is `WHERE last_request < cutoff` over the whole table.
    index('rate_limit_last_request_idx').on(t.lastRequest),
    check('rate_limit_count_nonneg', sql`${t.count} >= 0`),
  ],
)

export type User = typeof user.$inferSelect
export type NewUser = typeof user.$inferInsert
export type Session = typeof session.$inferSelect
export type RateLimit = typeof rateLimit.$inferSelect
export type NewRateLimit = typeof rateLimit.$inferInsert
